import { useEffect, useRef, useCallback } from 'react';
import { getUserEmail } from '@/lib/user';

const HUB_ID = import.meta.env['VITE_HUBSPOT_HUB_ID'];
const HUB_SPOT_SCRIPT_ID = 'hs-script-loader';

declare global {
  interface Window {
    _hsq?: HubSpotQueue[];
  }
}

type HubSpotQueue =
  | ['identify', { email: string }]
  | ['setPath', string]
  | ['setContentType', string]
  | ['trackPageView'];

function ensureHubSpotScriptLoaded(): void {
  if (document.getElementById(HUB_SPOT_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.src = `//js.hs-scripts.com/${HUB_ID}.js`;
  script.async = true;
  script.defer = true;
  script.type = 'text/javascript';
  script.id = HUB_SPOT_SCRIPT_ID;
  document.body.appendChild(script);
}

export function useHubSpot() {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!HUB_ID || initializedRef.current) return;

    ensureHubSpotScriptLoaded();
    initializedRef.current = true;
  }, []);

  // https://developers.hubspot.com/docs/reference/api/analytics-and-events/tracking-code
  // https://knowledge.hubspot.com/reports/understand-attribution-reporting#bucket-credits-from-your-externally-hosted-pages-to-a-specific-asset-type
  const trackPageView = useCallback(() => {
    const email = getUserEmail();
    if (!initializedRef.current || typeof window === 'undefined' || !email) return;

    const _hsq = (window._hsq = window._hsq || []);
    _hsq.push(['identify', { email }]);
    _hsq.push(['setPath', window.location.pathname]);
    _hsq.push(['setContentType', 'standard-page']);
    _hsq.push(['trackPageView']);
  }, []);

  return { trackPageView };
}
